/**
 * Shared formatting utilities for subscription pricing display.
 *
 * The pricing model transitioned from flat `basePrice` to per-clinical-user
 * pricing (`monthlyPerClinicalUser`). These helpers handle both models
 * gracefully, preventing NaN when `basePrice` is undefined.
 */

import { SubscriptionPlan } from '../stores/subscriptionStore';

/**
 * Returns the effective display price for a plan.
 * Prefers per-clinical-user pricing over legacy basePrice.
 */
export function getEffectivePrice(plan: SubscriptionPlan): number {
  return plan.pricing.monthlyPerClinicalUser || plan.pricing.basePrice || 0;
}

/**
 * Formats a price with currency and billing cycle label.
 * Safely handles undefined/NaN values by defaulting to 0.
 */
export function formatPrice(price: number | undefined | null, cycle: string): string {
  const safePrice = Number(price) || 0;
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(safePrice);
  const cycleLabel = cycle === 'monthly' ? '/mo' : cycle === 'quarterly' ? '/qtr' : '/yr';
  return `${formatted}${cycleLabel}`;
}

/**
 * Formats a limit value, showing "Unlimited" for -1.
 */
export function formatLimit(limit: number): string {
  if (limit === -1) return 'Unlimited';
  return limit.toLocaleString();
}
